import type { ClientErrorKind, ClientErrorPayload } from "@unbox/api";
import { APIError } from "@/api/error";

export type AuthClientFailure = { code?: string; message?: string; status: number; statusText: string };

type AuthClientErrorKind = Extract<
  ClientErrorKind,
  "AuthorisationError" | "ForbiddenError" | "TooManyRequestsError" | "ValidationError" | "InternalServerError"
>;

const kindForStatus = (status: number): AuthClientErrorKind => {
  if (status === 401) return "AuthorisationError";
  if (status === 403) return "ForbiddenError";
  if (status === 429) return "TooManyRequestsError";
  if (status >= 400 && status < 500) return "ValidationError";
  return "InternalServerError";
};

export class AuthClientError extends APIError {
  readonly code: string | undefined;

  constructor(failure: AuthClientFailure) {
    const kind = kindForStatus(failure.status);
    const payload: ClientErrorPayload = {
      kind,
      message: failure.message || "Something went wrong, please try again later",
      display: kind !== "ForbiddenError",
    };
    const status = failure.status >= 200 && failure.status <= 599 ? failure.status : 500;
    super(payload, new Response(JSON.stringify(payload), { status, statusText: failure.statusText }));
    this.code = failure.code;
  }
}
